import { useRef, useEffect } from 'react';
import difference from 'lodash/difference';
import isNull from 'lodash/fp/isNull';
import isUndefined from 'lodash/fp/isUndefined';
import omitBy from 'lodash/fp/omitBy';
import { metrics } from '@atlassian/browser-metrics';

// eslint-disable-next-line jira/restricted/@atlassian+jira-relay-utils
import { createRelayResource } from '@atlassian/jira-relay-utils';
import QUERY, {
	type mainIssueHierarchyQuery,
	type mainIssueHierarchyQuery$data,
} from '@atlassian/jira-relay/src/__generated__/mainIssueHierarchyQuery.graphql';
import {
	METRICS_PAGE_ID,
	JIRA_LONG_RUNNING_TASK_STATUS,
	UPDATE_HIERARCHY_TASK_STATUS,
	HIERARCHY_LEVELS_TO_IGNORE_ISSUE_TYPES,
} from '../common/constants';
import type {
	IssueHierarchyResponse,
	IssueHierarchyConfig,
	IssueHierarchyConfigPayload,
} from '../common/types';

export const issueHierarchyPageResource = createRelayResource<mainIssueHierarchyQuery>({
	type: 'RESOURCE1',
	getQuery: (_, { tenantContext }) => ({
		parameters: QUERY,
		variables: {
			cloudId: tenantContext.cloudId,
		},
	}),
});

// Defaults for issue type
const DEFAULT_ISSUE_TYPE = {
	description: '',
	type: '',
	iconUrl: '',
	relatedSchemes: [],
};

const transformUpdateTaskStatus = (
	issueHierarchyResponse: mainIssueHierarchyQuery$data,
): (typeof UPDATE_HIERARCHY_TASK_STATUS)[keyof typeof UPDATE_HIERARCHY_TASK_STATUS] => {
	const taskProgress = issueHierarchyResponse.jira?.issueHierarchyConfigUpdateTask?.taskProgress;
	if (isNull(taskProgress) || isUndefined(taskProgress)) {
		return UPDATE_HIERARCHY_TASK_STATUS.NULL;
	}

	switch (taskProgress.status) {
		case JIRA_LONG_RUNNING_TASK_STATUS.COMPLETE:
			return UPDATE_HIERARCHY_TASK_STATUS.COMPLETED;
		case JIRA_LONG_RUNNING_TASK_STATUS.FAILED:
		case JIRA_LONG_RUNNING_TASK_STATUS.DEAD:
			return UPDATE_HIERARCHY_TASK_STATUS.FAILED;
		case JIRA_LONG_RUNNING_TASK_STATUS.ENQUEUED:
		case JIRA_LONG_RUNNING_TASK_STATUS.RUNNING:
		case JIRA_LONG_RUNNING_TASK_STATUS.CANCEL_REQUESTED:
			return UPDATE_HIERARCHY_TASK_STATUS.RUNNING;
		default:
			throw new Error(`No supported task status for IPA removal  ${taskProgress.status}`);
	}
};

export const transformIssueHierarchyResponse = (
	issueHierarchyResponse: mainIssueHierarchyQuery$data,
): IssueHierarchyResponse => {
	const { jira } = issueHierarchyResponse;
	const updateHierarchyTaskStatus = transformUpdateTaskStatus(issueHierarchyResponse);
	const issueHierarchyLimitsData = jira?.issueHierarchyLimits;

	const lockedIssueTypeIds = jira?.lockedIssueTypeIds || [];
	const useDataFromUpdateTask =
		updateHierarchyTaskStatus !== UPDATE_HIERARCHY_TASK_STATUS.NULL &&
		updateHierarchyTaskStatus !== UPDATE_HIERARCHY_TASK_STATUS.COMPLETED;

	const issueHierarchyConfigData =
		(useDataFromUpdateTask
			? issueHierarchyResponse.jira?.issueHierarchyConfigUpdateTask?.issueHierarchyConfig
			: jira?.issueHierarchyConfig?.data) || [];

	if (!issueHierarchyLimitsData) {
		throw new Error('Issue hierarchy response is invalid');
	}

	const { nameLength, maxLevels } = issueHierarchyLimitsData;

	return {
		// @ts-expect-error - TS2322 - Type '({ level: number; name: string; issueTypes: ({ description: ""; type: ""; iconUrl: ""; relatedSchemes: readonly []; } | null)[]; } | null)[]' is not assignable to type 'IssueHierarchyConfig'.
		issueHierarchyConfig: issueHierarchyConfigData
			.map((level) => {
				// Makes no sense to continue if there is no level
				if (level.hierarchyLevel?.level == null || level.hierarchyLevel?.name == null) {
					return null;
				}
				const levelNumber = level.hierarchyLevel.level;
				const levelName = level.hierarchyLevel.name;

				const issueTypes = level.cmpIssueTypes?.edges
					?.map((issueTypeNode) => {
						const hasId = issueTypeNode?.node?.id != null;

						// If the issue type doesn't have an id we can't continue
						if (issueTypeNode?.node?.issueTypeId == null || !hasId) {
							return null;
						}

						const { name, avatar, issueTypeId, description } = issueTypeNode.node;

						const issueType = {
							id: issueTypeId,
							name,
							hierarchyLevel: levelNumber,
							iconUrl: avatar?.small,
							type: issueTypeId,
							description,
						};

						return {
							...DEFAULT_ISSUE_TYPE,
							...omitBy(isNull, issueType),
						};
					})
					.filter(Boolean);

				// We want empty array for ignored levels or if issueTypes is null (invalid data from gql)
				const maybeIssueTypeLevel =
					HIERARCHY_LEVELS_TO_IGNORE_ISSUE_TYPES.includes(levelNumber) || !issueTypes
						? []
						: issueTypes;

				return {
					level: levelNumber,
					name: levelName,
					issueTypes: maybeIssueTypeLevel,
				};
			})
			.filter(Boolean),
		issueHierarchyLimits: {
			nameLength,
			maxLevels,
		},
		lockedIssueTypeIds,
		updateHierarchyTaskStatus,
	};
};

/*
 * The api spits out erros already with i18n, we just need to use it as is
 */

export const pageLoadMetrics = metrics.pageLoad({
	key: METRICS_PAGE_ID,
	ssr: { doneAsFmp: true },
});

export const areIssueParentAssociationsInvalidated = (
	prevConfig: IssueHierarchyConfig,
	newConfig: IssueHierarchyConfigPayload | null,
): boolean =>
	prevConfig.some(({ level, issueTypes }) => {
		if (!issueTypes || issueTypes.length === 0) return false;

		// if a level from the prev hierarchy config doesn't exist in current hierarchy config
		const levelInNewConfig =
			newConfig && newConfig.find((newLevelData) => newLevelData.level === level);
		if (!levelInNewConfig) {
			return true;
		}

		// or an issueType has been removed from a hierarchy level
		// (this can be explicit via issueType removal, or implicit via level re-ordering)
		const currentIssueTypeIdsForLevel = issueTypes.map(({ id }) => id);
		const issueTypeRemoved = difference(
			currentIssueTypeIdsForLevel,
			levelInNewConfig.issueTypes || [],
		);
		if (issueTypeRemoved.length > 0) {
			return true;
		}

		// or an issueType has been added to a hierarchy level
		// (all issues are story-level (0) by default, so adding them to any level above 0 could break parent associations)
		const issueTypeAdded = difference(
			levelInNewConfig.issueTypes || [],
			currentIssueTypeIdsForLevel,
		);
		if (issueTypeAdded.length > 0) {
			return true;
		}

		return false;
	});

export function useRelaxedInterval(callback: () => void, delay: null | number) {
	const savedCallback = useRef<() => void>();

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		let running = false;
		let savedTimeout: NodeJS.Timeout | undefined | null = null;

		const tick = async () => {
			if (running && !isUndefined(savedCallback.current)) {
				await savedCallback.current();
			}

			if (running && !isNull(delay)) {
				savedTimeout = setTimeout(tick, delay);
			}
		};

		const stop = () => {
			running = false;
			const timeout = savedTimeout;

			if (timeout !== null) {
				clearTimeout(timeout);
			}
		};

		if (delay !== null) {
			running = true;
			savedTimeout = setTimeout(tick, delay);
			return stop;
		}
	}, [delay]);
}
